import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import personService from '../services/person.service';
import { useAuth } from './AuthContext';

const PersonContext = createContext(null);

export const PersonProvider = ({ children }) => {
  const { user } = useAuth();
  const [persons, setPersons] = useState([]);
  const [activePerson, setActivePerson] = useState(null);
  const [loading, setLoading] = useState(() => !!localStorage.getItem('token'));
  const [fetchError, setFetchError] = useState(false);

  const fetchPersons = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setPersons([]);
      setActivePerson(null);
      return;
    }
    setLoading(true);
    try {
      const res = await personService.getPersons();
      setPersons(res.data);
      setActivePerson((prev) => prev || (res.data.length > 0 ? res.data[0] : null));
      setFetchError(false);
    } catch (err) {
      console.error(err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  const createPerson = async (data) => {
    const res = await personService.createPerson(data);
    setPersons((prev) => [...prev, res.data]);
    setActivePerson(res.data);
    return res.data;
  };

  const updatePerson = async (id, data) => {
    const res = await personService.updatePerson(id, data);
    setPersons((prev) => prev.map((p) => (p._id === id ? res.data : p)));
    if (activePerson?._id === id) setActivePerson(res.data);
    return res.data;
  };

  const deletePerson = async (id) => {
    await personService.deletePerson(id);
    setPersons((prev) => prev.filter((p) => p._id !== id));
    if (activePerson?._id === id) setActivePerson(null);
  };

  return (
    <PersonContext.Provider value={{ persons, activePerson, setActivePerson, loading, fetchError, fetchPersons, createPerson, updatePerson, deletePerson }}>
      {children}
    </PersonContext.Provider>
  );
};

export const usePerson = () => {
  const ctx = useContext(PersonContext);
  if (!ctx) throw new Error('usePerson must be used within PersonProvider');
  return ctx;
};
